import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { propertiesAPI, ratesAPI, currenciesAPI, cloudinaryAPI, paymentGatewaysAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft, Save, Loader2, Building2, MapPin, Image, ListChecks, DollarSign,
  FileText, Plus, Trash2, Clock, Users, Bed, Bath, X, ChevronRight, Upload,
  CreditCard, Smartphone, Globe, Link, CheckCircle, XCircle, ImageIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPES = ['apartment', 'house', 'villa', 'condo', 'studio', 'cabin', 'cottage', 'loft', 'townhouse', 'bungalow'];

const AMENITY_CATEGORIES = {
  'Essentials': ['wifi', 'kitchen', 'ac', 'heating', 'washer', 'dryer', 'tv', 'iron', 'hair_dryer', 'coffee_maker', 'hangers', 'bed_linens', 'towels', 'shampoo'],
  'Safety': ['smoke_detector', 'carbon_monoxide_detector', 'fire_extinguisher', 'first_aid_kit', 'safe'],
  'Outdoor': ['pool', 'hot_tub', 'bbq', 'garden', 'terrace', 'balcony', 'patio', 'beach_access', 'lake_access'],
  'Parking & Access': ['parking', 'ev_charger', 'elevator', 'wheelchair_access', 'doorman'],
  'Entertainment': ['board_games', 'books', 'game_console', 'sound_system', 'projector'],
  'Kitchen & Dining': ['dishwasher', 'oven', 'microwave', 'refrigerator', 'stove', 'toaster', 'blender', 'cookware'],
  'Family': ['crib', 'high_chair', 'baby_bath', 'children_books', 'baby_monitor'],
  'Work': ['workspace', 'printer', 'office_chair'],
  'Winter': ['fireplace', 'ski_storage', 'ski_in_out', 'heated_floors'],
};

const FACILITY_OPTIONS = [
  'pool', 'hot_tub', 'sauna', 'gym', 'spa', 'garden', 'terrace', 'rooftop',
  'beach_front', 'lake_front', 'mountain_view', 'city_view', 'ocean_view',
  'ski_in_out', 'golf_course', 'tennis_court', 'restaurant', 'bar',
  'concierge', 'doorman', 'laundry_service', 'room_service', 'elevator',
  'business_center', 'meeting_rooms', 'kids_club', 'playground',
  'bicycle_rental', 'boat_dock', 'helipad', 'vineyard',
];

const TABS = [
  { id: 'details', label: 'Details', icon: FileText },
  { id: 'branding', label: 'Logo & Branding', icon: ImageIcon },
  { id: 'amenities', label: 'Amenities & Facilities', icon: ListChecks },
  { id: 'photos', label: 'Photo Gallery', icon: Image },
  { id: 'location', label: 'Location', icon: MapPin },
  { id: 'rates', label: 'Rates & Pricing', icon: DollarSign },
  { id: 'payments', label: 'Payment Gateways', icon: CreditCard },
];

const PropertySetupPage = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'details';
  const isNew = !propertyId;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    name: '', property_type: 'apartment', address: '', city: '', state: '', country: '',
    postal_code: '', latitude: 0, longitude: 0, neighborhood: '',
    bedrooms: 1, bathrooms: 1, max_guests: 2, beds: 1,
    description: '', short_description: '', house_rules: '', check_in_instructions: '',
    check_in_time: '15:00', check_out_time: '11:00',
    base_price: 0, cleaning_fee: 0, extra_guest_fee: 0, extra_guest_threshold: 1,
    security_deposit: 0, currency: 'USD', min_stay: 1, max_stay: 365,
    amenities: [], facilities: [], photos: [], image_url: '',
    logo_url: '',  // Property logo
    auto_cancel_hours: 48,  // Hours before auto-cancelling unpaid bookings
    enable_payment_reminders: true,  // Send reminder emails
    reminder_hours_before_expiry: 24  // Hours before expiry to send reminder
  });

  const [rates, setRates] = useState({
    seasonal_rates: [], occupancy_rates: [], los_discounts: [],
    weekend_markup_percent: 0, last_minute_discount_percent: 0, early_bird_discount_percent: 0
  });
  const [currencies, setCurrencies] = useState({});
  
  // Cloudinary state
  const [cloudinaryConfigured, setCloudinaryConfigured] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  
  // Payment gateway state
  const [paymentConfig, setPaymentConfig] = useState({
    use_host_settings: true,
    stripe: { enabled: false, api_key: '', publishable_key: '' },
    mpesa: { enabled: false, consumer_key: '', consumer_secret: '', shortcode: '', passkey: '', environment: 'sandbox' },
    pesapal: { enabled: false, consumer_key: '', consumer_secret: '', environment: 'sandbox', ipn_id: '' },
    preferred_gateway: null
  });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentTestResult, setPaymentTestResult] = useState(null);

  const loadCurrencies = useCallback(async () => {
    try {
      const resp = await currenciesAPI.list();
      setCurrencies(resp.data);
    } catch {}
  }, []);
  
  const loadCloudinaryStatus = useCallback(async () => {
    try {
      const resp = await cloudinaryAPI.status();
      setCloudinaryConfigured(resp.data.configured);
    } catch {}
  }, []);
  
  const loadPaymentConfig = useCallback(async () => {
    if (isNew) return;
    try {
      const resp = await paymentGatewaysAPI.getPropertyConfig(propertyId);
      setPaymentConfig(resp.data);
    } catch (err) {
      console.error('Payment config load error:', err);
    }
  }, [propertyId, isNew]);

  const loadProperty = useCallback(async () => {
    if (isNew) return;
    try {
      const [propResp, ratesResp] = await Promise.all([
        propertiesAPI.get(propertyId),
        ratesAPI.get(propertyId)
      ]);
      setForm(propResp.data);
      setRates(ratesResp.data);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [propertyId, isNew]);

  useEffect(() => { 
    loadProperty(); 
    loadCurrencies(); 
    loadCloudinaryStatus();
    loadPaymentConfig();
  }, [loadProperty, loadCurrencies, loadCloudinaryStatus, loadPaymentConfig]);

  const handleChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    handleChange(name, type === 'number' ? (value === '' ? 0 : Number(value)) : value);
  };

  const toggleAmenity = (amenity) => {
    setForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
    setSaved(false);
  };

  const toggleFacility = (facility) => {
    setForm(prev => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter(f => f !== facility)
        : [...prev.facilities, facility]
    }));
    setSaved(false);
  };

  // Photo management
  const addPhoto = () => {
    setForm(prev => ({
      ...prev,
      photos: [...prev.photos, { url: '', caption: '', tag: 'interior', order: prev.photos.length }]
    }));
  };

  const updatePhoto = (index, field, value) => {
    setForm(prev => {
      const photos = [...prev.photos];
      photos[index] = { ...photos[index], [field]: value };
      return { ...prev, photos };
    });
    setSaved(false);
  };

  const removePhoto = (index) => {
    setForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
    setSaved(false);
  };

  // File upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !propertyId) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('ihostpms_token');
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${BACKEND_URL}/api/photos/upload/${propertyId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (resp.ok) {
        const photo = await resp.json();
        setForm(prev => ({ ...prev, photos: [...prev.photos, photo] }));
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Logo upload handlers
  const handleLogoUrlChange = (value) => {
    setForm(prev => ({ ...prev, logo_url: value }));
    setSaved(false);
  };

  const handleLogoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if Cloudinary is configured
    if (!cloudinaryConfigured) {
      alert('Cloudinary is not configured. Please use a direct URL instead, or contact your administrator to set up Cloudinary.');
      e.target.value = '';
      return;
    }
    
    setLogoUploading(true);
    try {
      // Get Cloudinary signature
      const sigResp = await cloudinaryAPI.signature({ 
        resource_type: 'image', 
        folder: `ihostpms/logos/${propertyId || 'new'}` 
      });
      const sig = sigResp.data;
      
      // Upload directly to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      const cloudinaryResp = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (cloudinaryResp.ok) {
        const result = await cloudinaryResp.json();
        setForm(prev => ({ ...prev, logo_url: result.secure_url }));
        setSaved(false);
      } else {
        alert('Failed to upload logo to Cloudinary');
      }
    } catch (err) {
      console.error('Logo upload error:', err);
      alert('Error uploading logo: ' + err.message);
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  // Payment config handlers
  const handlePaymentConfigChange = (gateway, field, value) => {
    setPaymentConfig(prev => ({
      ...prev,
      [gateway]: {
        ...prev[gateway],
        [field]: value
      }
    }));
    setPaymentTestResult(null);
  };

  const handlePaymentConfigSave = async () => {
    if (!propertyId) return;
    setPaymentSaving(true);
    try {
      await paymentGatewaysAPI.updatePropertyConfig(propertyId, paymentConfig);
      setPaymentTestResult({ success: true, message: 'Payment configuration saved successfully' });
      setTimeout(() => setPaymentTestResult(null), 3000);
    } catch (err) {
      console.error('Payment config save error:', err);
      setPaymentTestResult({ success: false, message: 'Failed to save payment configuration' });
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleTestPesapal = async () => {
    try {
      const resp = await paymentGatewaysAPI.testPesapal();
      setPaymentTestResult(resp.data);
    } catch (err) {
      setPaymentTestResult({ success: false, message: 'Pesapal test failed: ' + (err.response?.data?.detail || err.message) });
    }
  };

  // Rate management helpers
  const addSeasonalRate = () => {
    setRates(prev => ({
      ...prev,
      seasonal_rates: [...prev.seasonal_rates, { name: '', start_date: '', end_date: '', base_price: 0, weekend_price: 0, min_stay: 1 }]
    }));
  };

  const updateSeasonalRate = (index, field, value) => {
    setRates(prev => {
      const rates = [...prev.seasonal_rates];
      rates[index] = { ...rates[index], [field]: field === 'name' ? value : (field.includes('date') ? value : Number(value)) };
      return { ...prev, seasonal_rates: rates };
    });
  };

  const removeSeasonalRate = (index) => {
    setRates(prev => ({ ...prev, seasonal_rates: prev.seasonal_rates.filter((_, i) => i !== index) }));
  };

  const addOccupancyRate = () => {
    setRates(prev => ({
      ...prev,
      occupancy_rates: [...prev.occupancy_rates, { guest_count: 1, price_modifier: 0 }]
    }));
  };

  const updateOccupancyRate = (index, field, value) => {
    setRates(prev => {
      const rates = [...prev.occupancy_rates];
      rates[index] = { ...rates[index], [field]: Number(value) };
      return { ...prev, occupancy_rates: rates };
    });
  };

  const removeOccupancyRate = (index) => {
    setRates(prev => ({ ...prev, occupancy_rates: prev.occupancy_rates.filter((_, i) => i !== index) }));
  };

  const addLOSDiscount = () => {
    setRates(prev => ({
      ...prev,
      los_discounts: [...prev.los_discounts, { min_nights: 7, discount_percent: 10 }]
    }));
  };

  const updateLOSDiscount = (index, field, value) => {
    setRates(prev => {
      const d = [...prev.los_discounts];
      d[index] = { ...d[index], [field]: Number(value) };
      return { ...prev, los_discounts: d };
    });
  };

  const removeLOSDiscount = (index) => {
    setRates(prev => ({ ...prev, los_discounts: prev.los_discounts.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let pid = propertyId;
      if (isNew) {
        const resp = await propertiesAPI.create(form);
        pid = resp.data.property_id;
      } else {
        await propertiesAPI.update(propertyId, form);
      }
      // Save rates
      await ratesAPI.update(pid, { ...rates, property_id: pid });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (isNew) navigate(`/properties/${pid}/setup`, { replace: true });
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" /></div>;

  return (
    <div data-testid="property-setup-page" className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/properties')} className="rounded-xl" data-testid="back-to-properties">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-airbnb-gray">{isNew ? 'Add Property' : form.name || 'Property Setup'}</h1>
            <p className="text-airbnb-medium-gray text-sm mt-0.5">Comprehensive property configuration for channel sync</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-emerald-600 font-medium animate-fade-in">Saved!</span>}
          <Button data-testid="property-setup-save" onClick={handleSave} disabled={saving} className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl px-6">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save All
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-2xl border border-gray-100 p-1.5 overflow-x-auto" data-testid="property-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-airbnb-pink text-white shadow-sm'
                : 'text-airbnb-medium-gray hover:bg-gray-50 hover:text-airbnb-gray'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        {/* ─── DETAILS TAB ─── */}
        {activeTab === 'details' && (
          <div className="space-y-6 animate-fade-in" data-testid="tab-content-details">
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-4 flex items-center gap-2"><Building2 className="w-5 h-5 text-airbnb-pink" /> Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <Label className="text-sm font-medium text-airbnb-gray">Property Name *</Label>
                  <Input data-testid="setup-name" name="name" value={form.name} onChange={handleInputChange} className="mt-1" placeholder="e.g. Sunset Beach Villa" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Property Type</Label>
                  <select name="property_type" value={form.property_type} onChange={handleInputChange} className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white" data-testid="setup-type">
                    {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-4 flex items-center gap-2"><Bed className="w-5 h-5 text-airbnb-pink" /> Capacity</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Bedrooms</Label>
                  <Input name="bedrooms" type="number" min="0" value={form.bedrooms} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Beds</Label>
                  <Input name="beds" type="number" min="1" value={form.beds} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Bathrooms</Label>
                  <Input name="bathrooms" type="number" min="0" value={form.bathrooms} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Max Guests</Label>
                  <Input name="max_guests" type="number" min="1" value={form.max_guests} onChange={handleInputChange} className="mt-1" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-airbnb-pink" /> Descriptions</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Short Description <span className="text-airbnb-medium-gray font-normal">(for channel listings)</span></Label>
                  <Input name="short_description" value={form.short_description} onChange={handleInputChange} className="mt-1" placeholder="Brief tagline for your property" maxLength={200} />
                  <p className="text-xs text-gray-400 mt-1">{form.short_description?.length || 0}/200</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Full Description <span className="text-airbnb-medium-gray font-normal">(min 50 chars for Airbnb)</span></Label>
                  <Textarea name="description" value={form.description} onChange={handleInputChange} className="mt-1" rows={5} placeholder="Detailed description of your property..." />
                  <p className="text-xs text-gray-400 mt-1">{form.description?.length || 0} characters</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-airbnb-pink" /> Guest Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">House Rules</Label>
                  <Textarea name="house_rules" value={form.house_rules} onChange={handleInputChange} className="mt-1" rows={3} placeholder="No smoking. No parties..." />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Check-in Instructions</Label>
                  <Textarea name="check_in_instructions" value={form.check_in_instructions} onChange={handleInputChange} className="mt-1" rows={3} placeholder="Self check-in via lockbox..." />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Check-in Time</Label>
                  <Input name="check_in_time" type="time" value={form.check_in_time} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Check-out Time</Label>
                  <Input name="check_out_time" type="time" value={form.check_out_time} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Minimum Stay (nights)</Label>
                  <Input name="min_stay" type="number" min="1" value={form.min_stay} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Maximum Stay (nights)</Label>
                  <Input name="max_stay" type="number" min="1" value={form.max_stay} onChange={handleInputChange} className="mt-1" />
                </div>
              </div>
              
              {/* Auto-cancellation setting */}
              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <Label className="text-sm font-semibold text-amber-800">Booking Auto-Cancellation</Label>
                <p className="text-xs text-amber-700 mb-3">Unpaid provisional bookings will be automatically cancelled after this period.</p>
                <div className="flex items-center gap-3">
                  <select
                    name="auto_cancel_hours"
                    value={form.auto_cancel_hours || 48}
                    onChange={handleInputChange}
                    className="w-40 h-10 px-3 border border-amber-300 rounded-lg text-sm bg-white text-amber-900"
                    data-testid="auto-cancel-hours"
                  >
                    <option value={12}>12 hours</option>
                    <option value={24}>24 hours</option>
                    <option value={48}>48 hours</option>
                    <option value={72}>72 hours</option>
                    <option value={168}>7 days</option>
                  </select>
                  <span className="text-sm text-amber-700">after booking is created</span>
                </div>
              </div>

              {/* Payment Reminder Settings */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-sm font-semibold text-blue-800">Payment Reminders</Label>
                    <p className="text-xs text-blue-600">Send email reminders to guests before provisional bookings expire</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={form.enable_payment_reminders ?? true}
                      onChange={e => setForm(f => ({ ...f, enable_payment_reminders: e.target.checked }))}
                      className="sr-only peer"
                      data-testid="enable-payment-reminders"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>
                {form.enable_payment_reminders && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-blue-700">Send reminder</span>
                    <select
                      name="reminder_hours_before_expiry"
                      value={form.reminder_hours_before_expiry || 24}
                      onChange={handleInputChange}
                      className="w-32 h-9 px-3 border border-blue-300 rounded-lg text-sm bg-white text-blue-900"
                      data-testid="reminder-hours"
                    >
                      <option value={6}>6 hours</option>
                      <option value={12}>12 hours</option>
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                    </select>
                    <span className="text-sm text-blue-700">before expiry</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── BRANDING TAB (Logo Upload) ─── */}
        {activeTab === 'branding' && (
          <div className="space-y-6 animate-fade-in" data-testid="tab-content-branding">
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-2 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-airbnb-pink" /> Property Logo
              </h3>
              <p className="text-sm text-airbnb-medium-gray mb-6">
                Upload your property logo to display on the booking widget, guest communications, and invoices.
              </p>

              {/* Current Logo Preview */}
              {form.logo_url && (
                <div className="mb-6">
                  <Label className="text-sm font-medium text-airbnb-gray mb-2 block">Current Logo</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-32 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200 flex items-center justify-center">
                      <img 
                        src={form.logo_url} 
                        alt="Property Logo" 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleLogoUrlChange('')}
                      className="text-red-500 hover:text-red-700 hover:border-red-300"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Remove Logo
                    </Button>
                  </div>
                </div>
              )}

              {/* Upload Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Option 1: Direct URL */}
                <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Link className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-airbnb-gray">Direct URL</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Enter a URL to an existing image hosted elsewhere.</p>
                  <Input
                    value={form.logo_url || ''}
                    onChange={(e) => handleLogoUrlChange(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="text-sm"
                    data-testid="logo-url-input"
                  />
                </div>

                {/* Option 2: Upload File */}
                <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Upload className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-airbnb-gray">Upload File</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {cloudinaryConfigured 
                      ? 'Cloudinary configured. Upload directly for optimal delivery.'
                      : 'Go to Settings > Cloudinary to add your credentials.'}
                  </p>
                  <input
                    type="file"
                    ref={logoInputRef}
                    onChange={handleLogoFileUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading || !cloudinaryConfigured}
                    className="w-full"
                    data-testid="logo-upload-btn"
                  >
                    {logoUploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> {cloudinaryConfigured ? 'Select File' : 'Configure Cloudinary First'}</>
                    )}
                  </Button>
                  {!cloudinaryConfigured && (
                    <p className="text-xs text-amber-600 mt-2">Add Cloudinary credentials in .env to enable uploads</p>
                  )}
                </div>

                {/* Option 3: Cloudinary Library (info) */}
                <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-airbnb-gray">Cloudinary CDN</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    For best performance, we recommend using Cloudinary for image hosting.
                  </p>
                  <div className={`text-xs px-3 py-2 rounded-lg ${cloudinaryConfigured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {cloudinaryConfigured ? (
                      <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Cloudinary configured</span>
                    ) : (
                      <span className="flex items-center gap-1"><XCircle className="w-3 h-3" /> Cloudinary not configured</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Logo Guidelines */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Logo Guidelines</h4>
                <ul className="text-xs text-blue-700 space-y-1 list-disc pl-4">
                  <li>Recommended size: 200x200 pixels minimum, square or landscape orientation</li>
                  <li>Supported formats: PNG (transparent background recommended), JPG, SVG</li>
                  <li>Maximum file size: 2MB</li>
                  <li>Your logo will appear on the booking widget, confirmation emails, and guest portal</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ─── AMENITIES TAB ─── */}
        {activeTab === 'amenities' && (
          <div className="space-y-8 animate-fade-in" data-testid="tab-content-amenities">
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-2">Amenities</h3>
              <p className="text-sm text-airbnb-medium-gray mb-4">Select all amenities available. Minimum 5 required for Airbnb listings.</p>
              <p className="text-sm font-medium text-airbnb-pink mb-4">{form.amenities.length} selected</p>
              {Object.entries(AMENITY_CATEGORIES).map(([category, items]) => (
                <div key={category} className="mb-5">
                  <h4 className="text-sm font-bold text-airbnb-gray mb-2">{category}</h4>
                  <div className="flex flex-wrap gap-2">
                    {items.map(amenity => (
                      <button
                        key={amenity}
                        type="button"
                        data-testid={`amenity-${amenity}`}
                        onClick={() => toggleAmenity(amenity)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          form.amenities.includes(amenity)
                            ? 'bg-airbnb-pink text-white border-airbnb-pink'
                            : 'bg-white text-airbnb-medium-gray border-gray-200 hover:border-airbnb-pink hover:text-airbnb-pink'
                        }`}
                      >
                        {amenity.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-airbnb-gray mb-2">Facilities</h3>
              <p className="text-sm text-airbnb-medium-gray mb-4">Property-level facilities (syncs with Booking.com Facilities API)</p>
              <p className="text-sm font-medium text-airbnb-pink mb-4">{form.facilities.length} selected</p>
              <div className="flex flex-wrap gap-2">
                {FACILITY_OPTIONS.map(facility => (
                  <button
                    key={facility}
                    type="button"
                    data-testid={`facility-${facility}`}
                    onClick={() => toggleFacility(facility)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      form.facilities.includes(facility)
                        ? 'bg-airbnb-teal text-white border-airbnb-teal'
                        : 'bg-white text-airbnb-medium-gray border-gray-200 hover:border-airbnb-teal hover:text-airbnb-teal'
                    }`}
                  >
                    {facility.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── PHOTOS TAB ─── */}
        {activeTab === 'photos' && (
          <div className="space-y-6 animate-fade-in" data-testid="tab-content-photos">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-airbnb-gray">Photo Gallery</h3>
                <p className="text-sm text-airbnb-medium-gray">Minimum 7 photos (3 high-res 800x500+) required for Airbnb. Ordered by priority.</p>
              </div>
              <Button onClick={addPhoto} className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl" size="sm" data-testid="add-photo-btn">
                <Plus className="w-4 h-4 mr-1" /> Add URL
              </Button>
              {!isNew && (
                <>
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                  <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" size="sm" className="rounded-xl" data-testid="upload-photo-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Upload File
                  </Button>
                </>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Cover Image URL</Label>
              <Input name="image_url" value={form.image_url} onChange={handleInputChange} className="mt-1" placeholder="https://..." />
              {form.image_url && (
                <img src={form.image_url} alt="Cover" className="mt-2 rounded-xl h-32 object-cover border" />
              )}
            </div>

            {form.photos.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-2xl">
                <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-airbnb-medium-gray mb-2">No photos added yet</p>
                <Button onClick={addPhoto} variant="outline" size="sm">Add Your First Photo</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {form.photos.map((photo, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl" data-testid={`photo-${idx}`}>
                    <div className="w-24 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      {photo.url ? <img src={photo.url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><Image className="w-6 h-6" /></div>}
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input value={photo.url} onChange={(e) => updatePhoto(idx, 'url', e.target.value)} placeholder="Image URL" className="col-span-3 h-8 text-xs" />
                      <Input value={photo.caption} onChange={(e) => updatePhoto(idx, 'caption', e.target.value)} placeholder="Caption" className="h-8 text-xs" />
                      <select value={photo.tag} onChange={(e) => updatePhoto(idx, 'tag', e.target.value)} className="h-8 px-2 border border-gray-200 rounded-md text-xs bg-white">
                        {['exterior', 'interior', 'bedroom', 'bathroom', 'kitchen', 'living', 'view', 'amenity', 'other'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400">#{idx + 1}</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removePhoto(idx)} className="h-8 w-8 p-0 text-gray-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-blue-50 rounded-xl p-4 text-xs text-blue-700">
              <p className="font-semibold mb-1">Channel Photo Requirements:</p>
              <ul className="space-y-0.5 list-disc pl-4">
                <li><strong>Airbnb:</strong> Min 7 photos, 3 must be high-res (800x500px+)</li>
                <li><strong>Booking.com:</strong> Auto-placed in property gallery, supports smart ordering</li>
                <li><strong>Expedia:</strong> High-quality images recommended, supports room-level photos</li>
              </ul>
            </div>
          </div>
        )}

        {/* ─── LOCATION TAB ─── */}
        {activeTab === 'location' && (
          <div className="space-y-6 animate-fade-in" data-testid="tab-content-location">
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-airbnb-pink" /> Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-airbnb-gray">Street Address</Label>
                  <Input name="address" value={form.address} onChange={handleInputChange} className="mt-1" placeholder="123 Ocean Drive, Apt 4B" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">City</Label>
                  <Input name="city" value={form.city} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">State / Province</Label>
                  <Input name="state" value={form.state} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Country</Label>
                  <Input name="country" value={form.country} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Postal Code</Label>
                  <Input name="postal_code" value={form.postal_code} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Neighborhood</Label>
                  <Input name="neighborhood" value={form.neighborhood} onChange={handleInputChange} className="mt-1" placeholder="e.g. SoHo, Malibu Beach" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-4">GPS Coordinates</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Latitude</Label>
                  <Input data-testid="setup-latitude" name="latitude" type="number" step="any" value={form.latitude} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Longitude</Label>
                  <Input data-testid="setup-longitude" name="longitude" type="number" step="any" value={form.longitude} onChange={handleInputChange} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Google Maps preview */}
            {form.latitude !== 0 && form.longitude !== 0 && (
              <div>
                <h3 className="text-lg font-bold text-airbnb-gray mb-4">Map Preview</h3>
                <div className="rounded-xl overflow-hidden border border-gray-200 h-64" data-testid="map-preview">
                  <iframe
                    title="Property Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps?q=${form.latitude},${form.longitude}&z=15&output=embed`}
                  />
                </div>
              </div>
            )}

            <div className="bg-amber-50 rounded-xl p-4 text-xs text-amber-700">
              <p className="font-semibold mb-1">Location Sync Notes:</p>
              <ul className="space-y-0.5 list-disc pl-4">
                <li>GPS coordinates are used by all channels for map placement</li>
                <li>Booking.com and Expedia use neighborhood for area-based search</li>
                <li>Exact address is hidden from guests until booking confirmation</li>
              </ul>
            </div>
          </div>
        )}

        {/* ─── RATES TAB ─── */}
        {activeTab === 'rates' && (
          <div className="space-y-8 animate-fade-in" data-testid="tab-content-rates">
            {/* Base pricing */}
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-airbnb-pink" /> Base Pricing</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Property Currency</Label>
                  <select name="currency" value={form.currency} onChange={handleInputChange} className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white" data-testid="property-currency">
                    {Object.entries(currencies).map(([code, config]) => (
                      <option key={code} value={code}>{config.symbol} {code}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-0.5">Currency for this property's rates</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Base Price / Night</Label>
                  <Input data-testid="setup-base-price" name="base_price" type="number" min="0" value={form.base_price} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Cleaning Fee</Label>
                  <Input name="cleaning_fee" type="number" min="0" value={form.cleaning_fee} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Security Deposit</Label>
                  <Input name="security_deposit" type="number" min="0" value={form.security_deposit} onChange={handleInputChange} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Extra Guest Fee</Label>
                  <Input name="extra_guest_fee" type="number" min="0" value={form.extra_guest_fee} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Extra Guest Threshold</Label>
                  <Input name="extra_guest_threshold" type="number" min="1" value={form.extra_guest_threshold} onChange={handleInputChange} className="mt-1" />
                  <p className="text-xs text-gray-400 mt-0.5">Fee applies after this many guests</p>
                </div>
              </div>
            </div>

            {/* Seasonal Rates */}
            <div className="border-t border-gray-100 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-airbnb-gray">Seasonal Rates</h3>
                  <p className="text-sm text-airbnb-medium-gray">Override base price for specific date ranges (syncs with Booking.com ARI API)</p>
                </div>
                <Button onClick={addSeasonalRate} size="sm" className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl" data-testid="add-seasonal-rate">
                  <Plus className="w-4 h-4 mr-1" /> Add Season
                </Button>
              </div>

              {rates.seasonal_rates.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No seasonal rates configured. Base price will apply year-round.</p>
              ) : (
                <div className="space-y-3">
                  {rates.seasonal_rates.map((sr, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 items-end p-3 bg-gray-50 rounded-xl" data-testid={`seasonal-rate-${idx}`}>
                      <div>
                        <Label className="text-xs text-gray-500">Season Name</Label>
                        <Input value={sr.name} onChange={(e) => updateSeasonalRate(idx, 'name', e.target.value)} className="h-9 text-sm" placeholder="Peak Summer" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Start Date</Label>
                        <Input type="date" value={sr.start_date} onChange={(e) => updateSeasonalRate(idx, 'start_date', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">End Date</Label>
                        <Input type="date" value={sr.end_date} onChange={(e) => updateSeasonalRate(idx, 'end_date', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Weekday Rate</Label>
                        <Input type="number" value={sr.base_price} onChange={(e) => updateSeasonalRate(idx, 'base_price', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Weekend Rate</Label>
                        <Input type="number" value={sr.weekend_price} onChange={(e) => updateSeasonalRate(idx, 'weekend_price', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeSeasonalRate(idx)} className="h-9 text-gray-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Occupancy-Based Pricing */}
            <div className="border-t border-gray-100 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-airbnb-gray">Occupancy-Based Pricing</h3>
                  <p className="text-sm text-airbnb-medium-gray">Add surcharges based on number of guests (compatible with Booking.com derived pricing)</p>
                </div>
                <Button onClick={addOccupancyRate} size="sm" className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl" data-testid="add-occupancy-rate">
                  <Plus className="w-4 h-4 mr-1" /> Add Tier
                </Button>
              </div>

              {rates.occupancy_rates.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No occupancy-based pricing. Same rate for all guest counts.</p>
              ) : (
                <div className="space-y-2">
                  {rates.occupancy_rates.map((occ, idx) => (
                    <div key={idx} className="flex items-end gap-3 p-3 bg-gray-50 rounded-xl" data-testid={`occupancy-rate-${idx}`}>
                      <div className="flex-1">
                        <Label className="text-xs text-gray-500">From # Guests</Label>
                        <Input type="number" min="1" value={occ.guest_count} onChange={(e) => updateOccupancyRate(idx, 'guest_count', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-gray-500">Price Modifier (+ / night)</Label>
                        <Input type="number" value={occ.price_modifier} onChange={(e) => updateOccupancyRate(idx, 'price_modifier', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeOccupancyRate(idx)} className="h-9 text-gray-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Length-of-Stay Discounts */}
            <div className="border-t border-gray-100 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-airbnb-gray">Length-of-Stay Discounts</h3>
                  <p className="text-sm text-airbnb-medium-gray">Encourage longer bookings with automatic discounts</p>
                </div>
                <Button onClick={addLOSDiscount} size="sm" className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl" data-testid="add-los-discount">
                  <Plus className="w-4 h-4 mr-1" /> Add Discount
                </Button>
              </div>

              {rates.los_discounts.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No length-of-stay discounts configured.</p>
              ) : (
                <div className="space-y-2">
                  {rates.los_discounts.map((los, idx) => (
                    <div key={idx} className="flex items-end gap-3 p-3 bg-gray-50 rounded-xl" data-testid={`los-discount-${idx}`}>
                      <div className="flex-1">
                        <Label className="text-xs text-gray-500">Minimum Nights</Label>
                        <Input type="number" min="1" value={los.min_nights} onChange={(e) => updateLOSDiscount(idx, 'min_nights', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-gray-500">Discount %</Label>
                        <Input type="number" min="0" max="100" value={los.discount_percent} onChange={(e) => updateLOSDiscount(idx, 'discount_percent', e.target.value)} className="h-9 text-sm" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeLOSDiscount(idx)} className="h-9 text-gray-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Global Rate Modifiers */}
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-airbnb-gray mb-4">Global Rate Modifiers</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Weekend Markup %</Label>
                  <Input data-testid="weekend-markup" type="number" min="0" value={rates.weekend_markup_percent} onChange={(e) => setRates(r => ({ ...r, weekend_markup_percent: Number(e.target.value) }))} className="mt-1" />
                  <p className="text-xs text-gray-400 mt-0.5">Applied to Fri/Sat nights</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Last-Minute Discount %</Label>
                  <Input type="number" min="0" value={rates.last_minute_discount_percent} onChange={(e) => setRates(r => ({ ...r, last_minute_discount_percent: Number(e.target.value) }))} className="mt-1" />
                  <p className="text-xs text-gray-400 mt-0.5">Bookings within 3 days</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Early Bird Discount %</Label>
                  <Input type="number" min="0" value={rates.early_bird_discount_percent} onChange={(e) => setRates(r => ({ ...r, early_bird_discount_percent: Number(e.target.value) }))} className="mt-1" />
                  <p className="text-xs text-gray-400 mt-0.5">Bookings 60+ days ahead</p>
                </div>
              </div>
            </div>

            <div className="bg-emerald-50 rounded-xl p-4 text-xs text-emerald-700">
              <p className="font-semibold mb-1">Channel Rate Sync Compatibility:</p>
              <ul className="space-y-0.5 list-disc pl-4">
                <li><strong>Airbnb:</strong> Nightly rates, cleaning fees, extra guest fees, minimum stay</li>
                <li><strong>Booking.com:</strong> Standard, derived (occupancy-based), and length-of-stay pricing</li>
                <li><strong>Agoda/Expedia:</strong> Dynamic rates, last-minute deals, promotional pricing</li>
                <li><strong>All channels:</strong> Weekend/weekday differentiation via seasonal or global markup</li>
              </ul>
            </div>
          </div>
        )}

        {/* ─── PAYMENT GATEWAYS TAB ─── */}
        {activeTab === 'payments' && !isNew && (
          <div className="space-y-6 animate-fade-in" data-testid="tab-content-payments">
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray mb-2 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-airbnb-pink" /> Payment Gateway Settings
              </h3>
              <p className="text-sm text-airbnb-medium-gray mb-6">
                Configure payment gateways for this property's booking widget. You can use your host-level settings or override them per property.
              </p>

              {/* Use Host Settings Toggle */}
              <div className="p-4 bg-gray-50 rounded-xl mb-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold text-airbnb-gray">Use Host-Level Settings</Label>
                    <p className="text-xs text-gray-500 mt-0.5">Enable to use your account-wide payment settings configured in Settings page</p>
                  </div>
                  <Switch
                    checked={paymentConfig.use_host_settings}
                    onCheckedChange={(checked) => setPaymentConfig(prev => ({ ...prev, use_host_settings: checked }))}
                    data-testid="use-host-settings"
                  />
                </div>
              </div>

              {!paymentConfig.use_host_settings && (
                <div className="space-y-6">
                  {/* Stripe Configuration */}
                  <div className="p-5 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-airbnb-gray">Stripe</h4>
                          <p className="text-xs text-gray-500">Accept credit/debit cards worldwide</p>
                        </div>
                      </div>
                      <Switch
                        checked={paymentConfig.stripe?.enabled || false}
                        onCheckedChange={(checked) => handlePaymentConfigChange('stripe', 'enabled', checked)}
                        data-testid="stripe-enabled"
                      />
                    </div>
                    {paymentConfig.stripe?.enabled && (
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                        <div>
                          <Label className="text-xs text-gray-500">Publishable Key</Label>
                          <Input
                            value={paymentConfig.stripe?.publishable_key || ''}
                            onChange={(e) => handlePaymentConfigChange('stripe', 'publishable_key', e.target.value)}
                            placeholder="pk_..."
                            className="mt-1 font-mono text-sm"
                            data-testid="stripe-publishable-key"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Secret Key</Label>
                          <Input
                            type="password"
                            value={paymentConfig.stripe?.api_key || ''}
                            onChange={(e) => handlePaymentConfigChange('stripe', 'api_key', e.target.value)}
                            placeholder="sk_..."
                            className="mt-1 font-mono text-sm"
                            data-testid="stripe-secret-key"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* M-Pesa Configuration */}
                  <div className="p-5 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Smartphone className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-airbnb-gray">M-Pesa</h4>
                          <p className="text-xs text-gray-500">Mobile money payments via Safaricom Daraja API</p>
                        </div>
                      </div>
                      <Switch
                        checked={paymentConfig.mpesa?.enabled || false}
                        onCheckedChange={(checked) => handlePaymentConfigChange('mpesa', 'enabled', checked)}
                        data-testid="mpesa-enabled-prop"
                      />
                    </div>
                    {paymentConfig.mpesa?.enabled && (
                      <div className="space-y-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Consumer Key</Label>
                            <Input
                              value={paymentConfig.mpesa?.consumer_key || ''}
                              onChange={(e) => handlePaymentConfigChange('mpesa', 'consumer_key', e.target.value)}
                              placeholder="From Daraja portal"
                              className="mt-1 font-mono text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Consumer Secret</Label>
                            <Input
                              type="password"
                              value={paymentConfig.mpesa?.consumer_secret || ''}
                              onChange={(e) => handlePaymentConfigChange('mpesa', 'consumer_secret', e.target.value)}
                              placeholder="From Daraja portal"
                              className="mt-1 font-mono text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Shortcode (Paybill/Till)</Label>
                            <Input
                              value={paymentConfig.mpesa?.shortcode || ''}
                              onChange={(e) => handlePaymentConfigChange('mpesa', 'shortcode', e.target.value)}
                              placeholder="e.g., 174379"
                              className="mt-1 font-mono text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Passkey</Label>
                            <Input
                              type="password"
                              value={paymentConfig.mpesa?.passkey || ''}
                              onChange={(e) => handlePaymentConfigChange('mpesa', 'passkey', e.target.value)}
                              placeholder="From Daraja portal"
                              className="mt-1 font-mono text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Environment</Label>
                          <select
                            value={paymentConfig.mpesa?.environment || 'sandbox'}
                            onChange={(e) => handlePaymentConfigChange('mpesa', 'environment', e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                          >
                            <option value="sandbox">Sandbox (Testing)</option>
                            <option value="production">Production (Live)</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pesapal Configuration */}
                  <div className="p-5 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Globe className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-airbnb-gray">Pesapal</h4>
                          <p className="text-xs text-gray-500">Multi-channel payments (cards, M-Pesa, bank transfers)</p>
                        </div>
                      </div>
                      <Switch
                        checked={paymentConfig.pesapal?.enabled || false}
                        onCheckedChange={(checked) => handlePaymentConfigChange('pesapal', 'enabled', checked)}
                        data-testid="pesapal-enabled-prop"
                      />
                    </div>
                    {paymentConfig.pesapal?.enabled && (
                      <div className="space-y-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Consumer Key</Label>
                            <Input
                              value={paymentConfig.pesapal?.consumer_key || ''}
                              onChange={(e) => handlePaymentConfigChange('pesapal', 'consumer_key', e.target.value)}
                              placeholder="From Pesapal dashboard"
                              className="mt-1 font-mono text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Consumer Secret</Label>
                            <Input
                              type="password"
                              value={paymentConfig.pesapal?.consumer_secret || ''}
                              onChange={(e) => handlePaymentConfigChange('pesapal', 'consumer_secret', e.target.value)}
                              placeholder="From Pesapal dashboard"
                              className="mt-1 font-mono text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Environment</Label>
                            <select
                              value={paymentConfig.pesapal?.environment || 'sandbox'}
                              onChange={(e) => handlePaymentConfigChange('pesapal', 'environment', e.target.value)}
                              className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                            >
                              <option value="sandbox">Sandbox (Testing)</option>
                              <option value="production">Production (Live)</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">IPN ID</Label>
                            <Input
                              value={paymentConfig.pesapal?.ipn_id || ''}
                              onChange={(e) => handlePaymentConfigChange('pesapal', 'ipn_id', e.target.value)}
                              placeholder="Auto-generated on registration"
                              className="mt-1 font-mono text-sm"
                              disabled
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleTestPesapal}
                            disabled={!paymentConfig.pesapal?.consumer_key}
                          >
                            Test Connection
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const resp = await paymentGatewaysAPI.registerPesapalIPN();
                                setPaymentConfig(prev => ({
                                  ...prev,
                                  pesapal: { ...prev.pesapal, ipn_id: resp.data.ipn_id }
                                }));
                                setPaymentTestResult({ success: true, message: 'IPN registered successfully' });
                              } catch (err) {
                                setPaymentTestResult({ success: false, message: 'IPN registration failed' });
                              }
                            }}
                            disabled={!paymentConfig.pesapal?.consumer_key}
                          >
                            Register IPN
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Preferred Gateway */}
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <Label className="text-sm font-semibold text-airbnb-gray mb-2 block">Preferred Gateway</Label>
                    <p className="text-xs text-gray-500 mb-3">Select which gateway to show first to guests, or leave empty to show all enabled options.</p>
                    <select
                      value={paymentConfig.preferred_gateway || ''}
                      onChange={(e) => setPaymentConfig(prev => ({ ...prev, preferred_gateway: e.target.value || null }))}
                      className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                    >
                      <option value="">Show all enabled gateways</option>
                      {paymentConfig.stripe?.enabled && <option value="stripe">Stripe (Cards)</option>}
                      {paymentConfig.mpesa?.enabled && <option value="mpesa">M-Pesa</option>}
                      {paymentConfig.pesapal?.enabled && <option value="pesapal">Pesapal</option>}
                    </select>
                  </div>

                  {/* Save Button & Test Result */}
                  {paymentTestResult && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${paymentTestResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {paymentTestResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      <span className="text-sm">{paymentTestResult.message}</span>
                    </div>
                  )}

                  <Button
                    onClick={handlePaymentConfigSave}
                    disabled={paymentSaving}
                    className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl"
                  >
                    {paymentSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Payment Settings
                  </Button>
                </div>
              )}

              {paymentConfig.use_host_settings && (
                <div className="text-center py-8 bg-blue-50 rounded-xl border border-blue-200">
                  <CreditCard className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                  <p className="text-blue-700 font-medium">Using host-level payment settings</p>
                  <p className="text-sm text-blue-600 mt-1">Configure payment gateways in Settings &gt; M-Pesa Payments</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => window.location.href = '/settings'}
                  >
                    Go to Settings
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'payments' && isNew && (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Save your property first to configure payment gateways</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default PropertySetupPage;
